/**
 * Tool Recommendation Matrix
 *
 * Canonical mapping of object type × output shape → recommended tool(s).
 * Source: DOCS_V4/00-canon/10B-object-type-output-shape-matrix.md
 *
 * Used by the ToolPicker to surface context-aware recommendations
 * when a configuration declares object_types and output_shapes.
 *
 * This is advisory — the compatibility evaluator still gates eligibility.
 * The matrix tells us what to recommend among compatible options.
 *
 * toolId values correspond to variant.toolId in src/lib/methodRegistry.ts.
 */

export type ObjectType = 'activities' | 'people' | 'places' | 'things' | 'information' | 'time';
export type OutputShape = 'ranking' | 'allocation' | 'sequencing' | 'filtering' | 'matching';

export interface MatrixRecommendation {
  /** Primary tool ID (matches variant.toolId in methodRegistry) */
  primaryToolId: string;
  /** Human-readable tool name for display */
  primaryToolName: string;
  /** Alternative tool IDs in preference order */
  alternativeToolIds: string[];
  /** Why this tool fits this combination */
  rationale: string;
  /**
   * True when this cell deliberately returns the same tool as the column's
   * default. Distinguishes "authored and honestly identical" from "unfilled".
   * PRD-method-recommendation §5 Phase 1; cells are authored with Harry.
   */
  sameAsDefault?: boolean;
  /**
   * Hazards the object type carries that the tool does not know about (scoring
   * people against a rubric has failure modes scoring vendors does not).
   * Shown verbatim beside the recommendation.
   */
  cautions?: string[];
}

/**
 * Object-type cautions that apply to every cell in the row (Doc 16 "Object
 * types with high political weight"). Merged into each recommendation's
 * `cautions` by getRecommendationsForConfiguration.
 */
export const OBJECT_TYPE_CAUTIONS: Record<ObjectType, string[]> = {
  activities: [],
  people: [
    'Decisions about people carry more political weight than decisions about work. Define and lock explicit criteria before anyone scores a person, and expect Catchball to matter even at a lightweight configuration.',
  ],
  places: [],
  things: [],
  information: [],
  time: [
    'Time is the thing being prioritized here, not the box everything fits into: which horizons or phases get attention. Most groups have not made this reframe; say it out loud before scoring.',
  ],
};

export const TOOL_RECOMMENDATION_MATRIX: Record<ObjectType, Record<OutputShape, MatrixRecommendation>> = {
  activities: {
    ranking: {
      primaryToolId: 'simple-weighted-scoring',
      primaryToolName: 'Weighted Scoring',
      alternativeToolIds: ['rice-scoring', 'wsjf', 'pairwise-ranking'],
      rationale: 'Weighted scoring maps initiative value against defined criteria',
    },
    allocation: {
      primaryToolId: 'buy-a-feature',
      primaryToolName: 'Buy A Feature',
      alternativeToolIds: ['simple-weighted-scoring'],
      rationale: 'Allocation tools distribute a finite resource across competing initiatives',
    },
    sequencing: {
      primaryToolId: 'cost-of-delay',
      primaryToolName: 'Cost of Delay',
      alternativeToolIds: ['wsjf'],
      rationale: 'Cost of delay surfaces the time-value of sequencing decisions',
    },
    filtering: {
      primaryToolId: 'moscow-method',
      primaryToolName: 'MoSCoW Method',
      alternativeToolIds: ['pareto-analysis'],
      rationale: 'MoSCoW and Pareto reduce large backlogs before detailed evaluation',
    },
    matching: {
      primaryToolId: 'simple-weighted-scoring',
      primaryToolName: 'Weighted Scoring',
      alternativeToolIds: [],
      rationale: 'Weighted scoring can evaluate fit of activities to teams or owners',
    },
  },
  people: {
    ranking: {
      primaryToolId: 'simple-weighted-scoring',
      primaryToolName: 'Weighted Scoring',
      alternativeToolIds: ['pairwise-ranking'],
      rationale: 'Rubric-based scoring against defined role criteria',
    },
    allocation: {
      primaryToolId: 'buy-a-feature',
      primaryToolName: 'Buy A Feature',
      alternativeToolIds: [],
      rationale: 'Distribute limited leadership attention or headcount across teams',
    },
    sequencing: {
      primaryToolId: 'cost-of-delay',
      primaryToolName: 'Cost of Delay',
      alternativeToolIds: [],
      rationale: 'Time-value of hiring or development sequencing',
    },
    filtering: {
      primaryToolId: 'moscow-method',
      primaryToolName: 'MoSCoW Method',
      alternativeToolIds: [],
      rationale: 'Reduce candidate pool to shortlist before detailed evaluation',
    },
    matching: {
      primaryToolId: 'simple-weighted-scoring',
      primaryToolName: 'Weighted Scoring',
      alternativeToolIds: ['pairwise-ranking'],
      rationale: 'Fit matrix — score candidates against role requirements',
    },
  },
  places: {
    ranking: {
      primaryToolId: 'simple-weighted-scoring',
      primaryToolName: 'Weighted Scoring',
      alternativeToolIds: ['impact-effort-matrix'],
      rationale: 'Weighted location comparison against market entry criteria',
    },
    allocation: {
      primaryToolId: 'buy-a-feature',
      primaryToolName: 'Buy A Feature',
      alternativeToolIds: [],
      rationale: 'Geographic resource distribution across markets',
    },
    sequencing: {
      primaryToolId: 'cost-of-delay',
      primaryToolName: 'Cost of Delay',
      alternativeToolIds: [],
      rationale: 'Market entry order by time-value',
    },
    filtering: {
      primaryToolId: 'moscow-method',
      primaryToolName: 'MoSCoW Method',
      alternativeToolIds: [],
      rationale: 'Reduce global market list to viable candidates',
    },
    matching: {
      primaryToolId: 'simple-weighted-scoring',
      primaryToolName: 'Weighted Scoring',
      alternativeToolIds: [],
      rationale: 'Match markets to product lines or teams',
    },
  },
  things: {
    ranking: {
      primaryToolId: 'simple-weighted-scoring',
      primaryToolName: 'Weighted Scoring',
      alternativeToolIds: ['pairwise-ranking'],
      rationale: 'Weighted comparison across vendors, tools, or assets',
    },
    allocation: {
      primaryToolId: 'buy-a-feature',
      primaryToolName: 'Buy A Feature',
      alternativeToolIds: [],
      rationale: 'Budget distribution across assets or investments',
    },
    sequencing: {
      primaryToolId: 'cost-of-delay',
      primaryToolName: 'Cost of Delay',
      alternativeToolIds: [],
      rationale: 'Acquisition or implementation order by time-value',
    },
    filtering: {
      primaryToolId: 'moscow-method',
      primaryToolName: 'MoSCoW Method',
      alternativeToolIds: [],
      rationale: 'Reduce longlist to shortlist before detailed evaluation',
    },
    matching: {
      primaryToolId: 'simple-weighted-scoring',
      primaryToolName: 'Weighted Scoring',
      alternativeToolIds: [],
      rationale: 'Match tools or assets to use cases or owners',
    },
  },
  information: {
    ranking: {
      primaryToolId: 'simple-weighted-scoring',
      primaryToolName: 'Weighted Scoring',
      alternativeToolIds: ['dot-voting'],
      rationale: 'Score signals by relevance and credibility',
    },
    allocation: {
      primaryToolId: 'buy-a-feature',
      primaryToolName: 'Buy A Feature',
      alternativeToolIds: [],
      rationale: 'Distribute analyst attention across information streams',
    },
    sequencing: {
      primaryToolId: 'cost-of-delay',
      primaryToolName: 'Cost of Delay',
      alternativeToolIds: [],
      rationale: 'What to act on in what order by urgency and importance',
    },
    filtering: {
      primaryToolId: 'pareto-analysis',
      primaryToolName: 'Pareto Analysis',
      alternativeToolIds: ['moscow-method'],
      rationale: 'Signal vs noise — Pareto identifies the 20% of inputs driving 80% of value',
    },
    matching: {
      primaryToolId: 'simple-weighted-scoring',
      primaryToolName: 'Weighted Scoring',
      alternativeToolIds: [],
      rationale: 'Match information sources to owners or workstreams',
    },
  },
  time: {
    ranking: {
      primaryToolId: 'dot-voting',
      primaryToolName: 'Dot Voting',
      alternativeToolIds: ['simple-weighted-scoring'],
      rationale: 'Quick collaborative ranking of time horizons or periods',
    },
    allocation: {
      primaryToolId: 'buy-a-feature',
      primaryToolName: 'Buy A Feature',
      alternativeToolIds: [],
      rationale: 'Distribute time budget across initiatives or phases',
    },
    sequencing: {
      primaryToolId: 'cost-of-delay',
      primaryToolName: 'Cost of Delay',
      alternativeToolIds: ['wsjf'],
      rationale: 'Phase ordering by time-value and dependency',
    },
    filtering: {
      primaryToolId: 'moscow-method',
      primaryToolName: 'MoSCoW Method',
      alternativeToolIds: [],
      rationale: 'Eliminate low-value time commitments before scheduling',
    },
    matching: {
      primaryToolId: 'simple-weighted-scoring',
      primaryToolName: 'Weighted Scoring',
      alternativeToolIds: [],
      rationale: 'Match time blocks to activities or people',
    },
  },
};

/**
 * Get the recommended tool for a given object type and output shape.
 * Returns null if the combination is not in the matrix (should not happen with valid inputs).
 */
export function getToolRecommendation(
  objectType: ObjectType,
  outputShape: OutputShape
): MatrixRecommendation | null {
  return TOOL_RECOMMENDATION_MATRIX[objectType]?.[outputShape] ?? null;
}

export interface ConfigurationRecommendations {
  /** Deduplicated primary tool IDs in recommendation order */
  primaryToolIds: string[];
  /** Map of toolId → recommendation details for display */
  byToolId: Record<string, MatrixRecommendation>;
}

/**
 * Get all recommendations for a configuration's declared object types and output shapes.
 * Returns deduplicated tool IDs in recommendation order with full recommendation details.
 */
export function getRecommendationsForConfiguration(
  objectTypes: ObjectType[],
  outputShapes: OutputShape[]
): ConfigurationRecommendations {
  const seen = new Set<string>();
  const primaryIds: string[] = [];
  const byToolId: Record<string, MatrixRecommendation> = {};

  for (const objectType of objectTypes) {
    for (const outputShape of outputShapes) {
      const rec = getToolRecommendation(objectType, outputShape);
      if (rec && !seen.has(rec.primaryToolId)) {
        seen.add(rec.primaryToolId);
        primaryIds.push(rec.primaryToolId);
        const cautions = [...(rec.cautions ?? []), ...OBJECT_TYPE_CAUTIONS[objectType]];
        byToolId[rec.primaryToolId] = cautions.length > 0 ? { ...rec, cautions } : rec;
      }
    }
  }

  return { primaryToolIds: primaryIds, byToolId };
}

/** Human-readable label map for object types */
export const OBJECT_TYPE_LABELS: Record<ObjectType, string> = {
  activities: 'Activities',
  people: 'People',
  places: 'Places',
  things: 'Things',
  information: 'Information',
  time: 'Time',
};

/** Banner copy for object type enforcement (Thing 1C). Empty string = no banner needed. */
export const OBJECT_TYPE_COPY: Record<ObjectType, string> = {
  activities: '',
  people: 'This prioritization is focused on People. Items should represent candidates, team members, or stakeholders.',
  places: 'This prioritization is focused on Places. Items should represent markets, locations, or geographies.',
  things: 'This prioritization is focused on Things. Items should represent vendors, tools, or assets.',
  information: 'This prioritization is focused on Information. Items should represent signals, data sources, or reports.',
  time: 'This prioritization is focused on Time. Items should represent time horizons, phases, or periods.',
};

/** Hint copy for output shape enforcement (Thing 1D). */
export const OUTPUT_SHAPE_HINTS: Record<OutputShape, string> = {
  ranking: 'This configuration produces a ranked list. Choose a ranking or scoring tool.',
  allocation: 'This configuration produces an allocation. Choose a tool that distributes a finite resource.',
  sequencing: 'This configuration produces a sequence. Choose a tool focused on ordering.',
  filtering: 'This configuration reduces a large set. Choose a screening or triage tool.',
  matching: 'This configuration pairs resources to needs. Choose a matching or fit-evaluation tool.',
};
